import React, { useContext, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { Separator } from '@/components/ui/separator';
import { SearchContext } from '../../features/project/building/Building';
import { useTypemarkContext } from '../../features/project/typemark/TypemarkContext';
import { Checkbox } from '../ui/Checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/RadioGroup';
import { navTestId } from '../../utils/testId';

interface FilterValue {
  value: string;
  title: string;
}

// Plain array = multi-select checkboxes (filter value is string[]),
// { type: 'radio' } = single-select radios (filter value is a scalar string).
type FilterGroup = FilterValue[] | { type: 'radio'; title?: string; options: FilterValue[] };

interface FilterProps {
  visible?: boolean;
  filteredValues?: Record<string, FilterGroup>;
  children: React.ReactNode;
  onClose?: () => void;
  onFilter?: (values: any) => void;
  value?: any;
}

function Filter({ filteredValues = {}, children }: FilterProps) {
  const [open, setOpen] = useState(false);
  const context = useTypemarkContext() || useContext(SearchContext);
  const { filter, setFilter } = context || {};

  const selectedFor = (key: string): string[] => {
    if (!filter) return [];
    return filter[key] || [];
  };

  const handleChange = (key: string, values: string[]) => {
    setFilter((prev: Record<string, string[]>) => ({ ...prev, [key]: values }));
  };

  const entries = Object.entries(filteredValues);
  if (entries.length === 0) return <>{children}</>;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span className="cursor-pointer">{children}</span>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-3 space-y-3">
        {entries.map(([key, group], groupIndex) => {
          if (!Array.isArray(group) && group?.type === 'radio') {
            return (
              <React.Fragment key={key}>
                {groupIndex > 0 && <Separator />}
                {group.title && (
                  <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                    {group.title}
                  </div>
                )}
                <RadioGroup
                  className="gap-1"
                  value={filter?.[key] != null ? String(filter[key]) : ''}
                  onValueChange={(value) =>
                    setFilter((prev: Record<string, unknown>) => ({ ...prev, [key]: value }))
                  }
                >
                  {(group.options || []).map((ele) => {
                    const id = `filter-${key}-${ele.value}`;
                    return (
                      <div key={String(ele.value)} className="flex items-center gap-2 py-0.5">
                        <RadioGroupItem
                          id={id}
                          value={String(ele.value)}
                          data-testid={`filter-radio-${key}-${navTestId(ele.title)}`}
                        />
                        <label
                          htmlFor={id}
                          className="text-sm text-neutral-900 cursor-pointer select-none"
                        >
                          {ele.title}
                        </label>
                      </div>
                    );
                  })}
                </RadioGroup>
              </React.Fragment>
            );
          }
          const values = group as FilterValue[];
          return (
            <React.Fragment key={key}>
              {groupIndex > 0 && <Separator />}
              <div className="space-y-1">
                {(values || []).map((ele, i) => {
                  const selected = selectedFor(key);
                  const isChecked = selected.includes(ele?.value);
                  const id = `filter-${key}-${i}`;
                  return (
                    <div key={i} className="flex items-center gap-2 py-0.5">
                      <Checkbox
                        id={id}
                        data-testid={`filter-checkbox-${key}-${i}`}
                        checked={isChecked}
                        onCheckedChange={(checked) => {
                          const newValues = checked
                            ? [...selected, ele?.value]
                            : selected.filter((v) => v !== ele?.value);
                          handleChange(key, newValues);
                        }}
                      />
                      <label
                        htmlFor={id}
                        className="text-sm text-neutral-900 cursor-pointer select-none"
                      >
                        {ele?.title}
                      </label>
                    </div>
                  );
                })}
              </div>
            </React.Fragment>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

export default Filter;
